import { CODIGOS_ERRO, schemaLogin, type EntradaLogin } from '@baiocchi/shared'
import { Alerta, Botao, Caixa, Campo, Entrada } from '@baiocchi/ui'
import { useState } from 'react'
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom'

import { useSessao } from '../../autenticacao/contexto-sessao'
import { useFormulario } from '../../ganchos/usar-formulario'
import { MolduraAutenticacao } from './moldura-autenticacao'

interface Destino {
  destino?: string
}

/**
 * TELA-00 — Login.
 *
 * A mensagem de erro é **sempre a que a API mandou**, e a API manda a mesma para
 * senha errada, e-mail inexistente e conta desativada (RN-01, RN-02). A tela não
 * enriquece esse texto: dizer "este e-mail não está cadastrado" seria devolver ao
 * atacante exatamente o que o contrato esconde.
 *
 * A única exceção é `CONTA_BLOQUEADA`, que ganha um parágrafo de orientação — ali
 * o bloqueio é fato conhecido pelo usuário legítimo e ele precisa saber que é
 * temporário.
 */
export function PaginaEntrar() {
  const { entrar, autenticado, carregando } = useSessao()
  const navegar = useNavigate()
  const local = useLocation()
  const [mostrarSenha, setMostrarSenha] = useState(false)

  const formulario = useFormulario<EntradaLogin>({
    email: '',
    senha: '',
    lembrarDispositivo: false,
  })

  if (!carregando && autenticado) {
    const destino = (local.state as Destino | null)?.destino ?? '/'
    return <Navigate to={destino} replace />
  }

  async function submeter(evento: React.FormEvent) {
    evento.preventDefault()

    const concluiu = await formulario.enviar(
      schemaLogin,
      (valores) => valores,
      async (corpo) => {
        const usuario = await entrar(corpo)
        const destino = (local.state as Destino | null)?.destino ?? '/'
        // RN-06: senha definida por terceiro obriga troca antes de qualquer coisa.
        await navegar(usuario.deveTrocarSenha ? '/perfil?trocar-senha=1' : destino, {
          replace: true,
        })
      },
    )

    if (!concluiu) formulario.definir('senha', '')
  }

  const erro = formulario.erroGeral
  // Pelo **código**, nunca por trecho da mensagem: o texto pode ser revisado sem
  // aviso, o código é o contrato (§14).
  const bloqueado =
    formulario.erroApi?.codigo === CODIGOS_ERRO.CONTA_BLOQUEADA ||
    formulario.erroApi?.codigo === CODIGOS_ERRO.LIMITE_REQUISICOES

  return (
    <MolduraAutenticacao
      titulo="Entrar no Baiocchi Leads"
      descricao="Use o e-mail corporativo cadastrado pelo administrador."
      rodape={
        <p>
          Não consegue entrar?{' '}
          <Link
            to="/esqueci-minha-senha"
            className="font-medium text-marca-600 underline-offset-2 hover:underline"
          >
            Recuperar acesso
          </Link>
        </p>
      }
    >
      <form onSubmit={submeter} className="flex flex-col gap-4" noValidate>
        {erro ? (
          <Alerta tom={bloqueado ? 'atencao' : 'perigo'}>
            {erro}
            {bloqueado ? (
              <p className="mt-1">
                O bloqueio é temporário e protege a conta de tentativas automatizadas. Aguarde
                alguns minutos antes de tentar de novo.
              </p>
            ) : null}
          </Alerta>
        ) : null}

        <Campo rotulo="E-mail" obrigatorio erro={formulario.erros.email}>
          {(atributos) => (
            <Entrada
              {...atributos}
              type="email"
              autoComplete="username"
              autoFocus
              inputMode="email"
              placeholder="nome@baiocchiimoveis.com.br"
              value={formulario.valores.email}
              temErro={atributos.temErro}
              onChange={(evento) => formulario.definir('email', evento.target.value)}
            />
          )}
        </Campo>

        <Campo
          rotulo="Senha"
          obrigatorio
          erro={formulario.erros.senha}
          acessorio={
            <button
              type="button"
              onClick={() => setMostrarSenha((valor) => !valor)}
              className="font-medium text-marca-600 hover:underline"
            >
              {mostrarSenha ? 'Ocultar' : 'Mostrar'}
            </button>
          }
        >
          {(atributos) => (
            <Entrada
              {...atributos}
              type={mostrarSenha ? 'text' : 'password'}
              autoComplete="current-password"
              value={formulario.valores.senha}
              temErro={atributos.temErro}
              onChange={(evento) => formulario.definir('senha', evento.target.value)}
            />
          )}
        </Campo>

        <Caixa
          rotulo="Manter-me conectado neste dispositivo"
          dica="Renova a sessão em segundo plano em vez de pedir login ao reabrir a aba."
          checked={formulario.valores.lembrarDispositivo ?? false}
          onChange={(evento) => formulario.definir('lembrarDispositivo', evento.target.checked)}
        />

        <Botao type="submit" larguraTotal tamanho="lg" carregando={formulario.enviando}>
          Entrar
        </Botao>
      </form>
    </MolduraAutenticacao>
  )
}
